import {
    AddressingCountryCode,
    getAddressingCountryMap,
    getAddressingCountryMapWithoutNames,
    toAddressingCountryCode,
    type AddressingCountryMapWithoutNames,
    type AddressingCountryMap,
    type AddressingCountry,
    type AddressingLocaleKey,
    AddressingCountryWithoutName,
} from '@precon-tools-suite-by-shared/js-commerceguys-addressing';
import { useI18n } from 'vue-i18n';
import { until, extendRef } from '@vueuse/core';
import {
    shallowRef,
    ref,
    computed,
    toValue,
    watch,
    readonly,
    type MaybeRefOrGetter,
} from 'vue';

export type UseAddressingCountryMapWithoutNamesOptions = {
    fallbackCountryCode?: MaybeRefOrGetter<AddressingCountryCode | undefined>;
};

export type UseAddressingCountryMapOptions =
    UseAddressingCountryMapWithoutNamesOptions & {
        locale?: MaybeRefOrGetter<string | null | undefined>;
        fallbackLocale?: AddressingLocaleKey;
        loadingTimeout?: number;
    };

export function useAddressingCountryMapWithoutNames({
    fallbackCountryCode,
}: UseAddressingCountryMapWithoutNamesOptions = {}) {
    const countryMap = shallowRef<AddressingCountryMapWithoutNames>(new Map());

    const loading = ref<boolean>(false);

    getAddressingCountryMapWithoutNames((val) => {
        loading.value = false;
        countryMap.value = val;
    });

    return {
        get(
            countryCode: string | null | undefined
        ): Readonly<AddressingCountryWithoutName> | undefined {
            const _countryCode =
                toAddressingCountryCode(countryCode) ??
                toValue(fallbackCountryCode);
            if (!_countryCode) return undefined;
            return countryMap.value.get(_countryCode);
        },
        values() {
            return countryMap.value.values();
        },
        loading: readonly(loading),
    };
}

/**
 * Loads the available addressing country map for the current locale.
 */
export function useAddressingCountryMap({
    locale,
    fallbackCountryCode,
    fallbackLocale = 'en',
    loadingTimeout = 2000,
}: UseAddressingCountryMapOptions = {}) {
    const { locale: defaultLocale } = useI18n();

    const countryMap = shallowRef<AddressingCountryMap>(new Map());

    const _locale = computed(() => toValue(locale) ?? defaultLocale.value);

    const loading = ref<boolean>(false);

    watch(
        () => _locale.value,
        async (locale) => {
            await until(loading).toBe(false, {
                timeout: loadingTimeout,
                throwOnTimeout: false,
            });

            try {
                countryMap.value = await getAddressingCountryMap({
                    locale,
                    fallbackLocale,
                });
            } finally {
                loading.value = false;
            }
        },
        { immediate: true }
    );

    return {
        get(
            countryCode: string | null | undefined
        ): Readonly<AddressingCountry> | undefined {
            const _countryCode =
                toAddressingCountryCode(countryCode) ??
                toValue(fallbackCountryCode);
            if (!_countryCode) return undefined;
            return countryMap.value.get(_countryCode);
        },
        values() {
            return countryMap.value.values();
        },
        loading: readonly(loading),
    };
}

export function useAddressingCountriesWithoutNames(
    options?: UseAddressingCountryMapWithoutNamesOptions
) {
    const { values, loading } = useAddressingCountryMapWithoutNames(options);

    const countries = computed(() => [...values()]);

    return extendRef(countries, {
        loading,
    });
}

/**
 * Loads and provides an array of all addressing countries for the current locale.
 */
export function useAddressingCountries(
    options?: UseAddressingCountryMapOptions
) {
    const { values, loading } = useAddressingCountryMap(options);

    const countries = computed(() => [...values()]);

    return extendRef(countries, {
        loading,
    });
}

export function useAddressingCountryWithoutName(
    countryCode: MaybeRefOrGetter<string | null | undefined>,
    options?: UseAddressingCountryMapWithoutNamesOptions
) {
    const { get, loading } = useAddressingCountryMapWithoutNames(options);

    const country = computed(() => get(toValue(countryCode)));

    return extendRef(country, { loading });
}

/**
 * Loads the country of the provided country code for the current locale.
 */
export function useAddressingCountry(
    countryCode: MaybeRefOrGetter<string | null | undefined>,
    options?: UseAddressingCountryMapOptions
) {
    const { get, loading } = useAddressingCountryMap(options);

    const country = computed(() => get(toValue(countryCode)));

    return extendRef(country, { loading });
}
